import { createContext, useContext, useEffect, useState, useRef, useCallback, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { Tables } from "@/integrations/supabase/types";
import { setUser as setSentryUser } from "@/lib/sentry";

type Profile = Tables<"profiles">;
type Business = Tables<"businesses">;
type Subscription = Tables<"subscriptions">;

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  business: Business | null;
  subscription: Subscription | null;
  isSubscriptionActive: boolean;
  isEmailVerified: boolean;
  loading: boolean;
  signUp: (email: string, password: string, name: string, role?: string, phone?: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signInWithGoogle: (redirectTo?: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  refreshSubscription: () => Promise<void>;
  sendVerificationEmail: () => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [business, setBusiness] = useState<Business | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Refs to prevent duplicate fetches and track initialization
  const profileFetchedRef = useRef<string | null>(null);
  const initializingRef = useRef(false);

  const fetchSubscription = async (businessId: string) => {
    const { data } = await supabase
      .from("subscriptions")
      .select("*")
      .eq("business_id", businessId)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    setSubscription(data);
  };

  const refreshSubscription = async () => {
    if (business?.id) {
      await fetchSubscription(business.id);
    }
  };

  const fetchProfile = useCallback(async (userId: string, force = false) => {
    // Prevent duplicate fetches for the same user
    if (!force && profileFetchedRef.current === userId) {
      return;
    }
    profileFetchedRef.current = userId;

    const { data } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .maybeSingle();

    let effectiveProfile = data;

    // Always get the current auth state to sync email verification
    const { data: authData } = await supabase.auth.getUser();
    const authUser = authData.user;
    const meta = (authUser?.user_metadata ?? {}) as Record<string, any>;
    const authEmailVerified = Boolean((authUser as any)?.email_confirmed_at) || Boolean(meta.email_verified);

    // If the profile row doesn't exist yet, create it from the auth user metadata.
    // If the profile row doesn't exist yet, create it from the auth user metadata.
    // IMPORTANT: New profiles always start unverified to ensure proper email verification flow
    // This prevents deleted users from bypassing verification when re-registering
    if (!effectiveProfile) {
      const { data: upserted } = await supabase
        .from("profiles")
        .upsert(
          {
            id: userId,
            name: (meta.name || meta.full_name || "User") as string,
            email: (authUser?.email || meta.email || "") as string,
            role: (meta.role || "customer") as any,
            phone: (meta.phone || null) as string | null,
            email_verified: false, // New profiles always start unverified
            account_status: "pending_verification", // Must verify email first
          },
          { onConflict: "id" }
        )
        .select("*")
        .maybeSingle();

      effectiveProfile = upserted ?? null;
    } else {
      // Profile exists - sync email_verified if auth says verified but profile doesn't
      if (authEmailVerified && !effectiveProfile.email_verified) {
        const { data: updatedProfile } = await supabase
          .from("profiles")
          .update({
            email_verified: true,
            account_status: "active",
          })
          .eq("id", userId)
          .select("*")
          .maybeSingle();
        
        effectiveProfile = updatedProfile ?? effectiveProfile;
      }
    }

    setProfile(effectiveProfile);

    // If user is a business owner, fetch or auto-create their business
    if (effectiveProfile && (effectiveProfile.role === "solo" || effectiveProfile.role === "center_admin")) {
      let businessData = null;
      
      // First, try to fetch existing business
      const { data: existingBusiness } = await supabase
        .from("businesses")
        .select("*")
        .eq("owner_user_id", userId)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      
      // If no business exists and email is verified, create one (safety net for trigger)
      if (!existingBusiness && effectiveProfile.email_verified) {
        console.log("[AuthContext] Auto-creating business for verified owner:", userId);
        
        // Determine subscription plan based on role
        const subscriptionPlan = effectiveProfile.role === 'solo' ? 'solo' : 'premium';
        
        const { data: newBusiness, error: createError } = await supabase
          .from("businesses")
          .insert({
            owner_user_id: userId,
            business_name: "", // Empty - user must fill in their business name
            category: "spa_wellness" as const, // Default, user can change
            email: effectiveProfile.email,
            address: "", // Empty - user must fill in
            subscription_plan: subscriptionPlan,
          })
          .select("*")
          .maybeSingle();
        
        if (createError) {
          console.error("[AuthContext] Failed to auto-create business:", createError);
        } else {
          businessData = newBusiness;
        }
      } else {
        businessData = existingBusiness;
      }
      
      setBusiness(businessData);

      // Fetch subscription for this business
      if (businessData?.id) {
        await fetchSubscription(businessData.id);
      }
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    const userId = user?.id ?? (await supabase.auth.getUser()).data.user?.id;
    if (userId) {
      await fetchProfile(userId, true); // Force refresh
    }
  }, [user?.id, fetchProfile]);

  // Check if email is verified
  const isEmailVerified = profile?.email_verified ?? false;

  // Calculate if subscription is active (includes trial period check)
  const isSubscriptionActive = (() => {
    // Solo plan is always active (free forever)
    if (subscription?.plan_type === 'solo' || business?.subscription_plan === 'solo') {
      return true;
    }
    
    // If has active subscription, it's active
    if (subscription?.status === "active") return true;
    
    // If cancelled or expired Premium, not active
    if (subscription?.status === "cancelled" || subscription?.status === "expired") return false;
    
    // If no subscription, check trial period (30 days from business creation)
    if (!subscription && business?.created_at) {
      const createdDate = new Date(business.created_at);
      const trialEndDate = new Date(createdDate);
      trialEndDate.setDate(trialEndDate.getDate() + 30);
      return new Date() < trialEndDate;
    }
    
    return true; // Default to active for edge cases
  })();

  // Helper function to log pending consent after signup
  const logPendingConsent = useCallback(async (userId: string) => {
    const pendingConsentStr = sessionStorage.getItem("pendingConsentLog");
    if (!pendingConsentStr) return;
    
    try {
      const consentInfo = JSON.parse(pendingConsentStr);
      sessionStorage.removeItem("pendingConsentLog");
      
      // Get IP address (best effort)
      let ipAddress = null;
      try {
        const ipResponse = await fetch("https://api.ipify.org?format=json");
        const ipData = await ipResponse.json();
        ipAddress = ipData.ip;
      } catch {
        // IP fetch failed, continue without it
      }
      
      // Log terms acceptance (now authenticated)
      await supabase.from("terms_acceptances").insert({
        user_id: userId,
        email: consentInfo.email,
        user_type: consentInfo.userType,
        terms_version: "1.0",
        privacy_version: "1.0",
        ip_address: ipAddress,
        user_agent: consentInfo.userAgent,
        device_info: {
          platform: consentInfo.platform,
          language: consentInfo.language,
          screenWidth: consentInfo.screenWidth,
          screenHeight: consentInfo.screenHeight,
          timezone: consentInfo.timezone,
        },
      });
      
      // Log to privacy_audit_logs for compliance
      await supabase.from("privacy_audit_logs").insert({
        user_id: userId,
        email: consentInfo.email,
        event_type: "registration_consent",
        user_agent: consentInfo.userAgent,
        ip_address: ipAddress,
        consent_version: "1.0",
        details: {
          terms_version: "1.0",
          privacy_version: "1.0",
          user_type: consentInfo.userType,
          marketing_consent: consentInfo.marketingConsent,
          platform: consentInfo.platform,
          language: consentInfo.language,
          timezone: consentInfo.timezone,
        },
      });
    } catch (error) {
      console.error("Error logging consent:", error);
    }
  }, []);

  useEffect(() => {
    // Prevent double initialization
    if (initializingRef.current) return;
    initializingRef.current = true;

    // Set up auth state listener FIRST
    const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);

        // Only fetch profile on sign-in events, not on every state change
        if (event === 'SIGNED_IN' && session?.user) {
          fetchProfile(session.user.id);
          // Log any pending consent from signup flow
          logPendingConsent(session.user.id);
          // Set user context for error tracking
          setSentryUser({ id: session.user.id, email: session.user.email });
        } else if (event === 'SIGNED_OUT') {
          setProfile(null);
          setBusiness(null);
          setSubscription(null);
          profileFetchedRef.current = null;
          // Clear user context for error tracking
          setSentryUser(null);
        }
        setLoading(false);
      }
    );

    // Check for existing session only once on mount
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
      }
      setLoading(false);
    });

    return () => authSubscription.unsubscribe();
  }, [fetchProfile, logPendingConsent]);

  const signUp = async (email: string, password: string, name: string, role: string = "customer", phone?: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          name,
          role,
          phone: phone || null,
        },
      },
    });

    // If signup successful, send verification email
    if (!error && data.user) {
      setTimeout(async () => {
        try {
          await supabase.functions.invoke("send-verification-email", {
            body: {
              userId: data.user!.id,
              email,
              name,
              userAgent: navigator.userAgent,
            },
          });
        } catch (e) {
          console.error("Error sending verification email:", e);
        }
      }, 1000);
    }

    return { error };
  };

  const sendVerificationEmail = async () => {
    if (!user || !profile) {
      return { error: new Error("User not logged in") };
    }

    try {
      const { error } = await supabase.functions.invoke("send-verification-email", {
        body: {
          userId: user.id,
          email: profile.email,
          name: profile.name,
          userAgent: navigator.userAgent,
        },
      });

      if (error) throw error;
      return { error: null };
    } catch (error: any) {
      return { error };
    }
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signInWithGoogle = async (redirectTo?: string) => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: redirectTo || `${window.location.origin}/`,
      },
    });
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setProfile(null);
    setBusiness(null);
    setSubscription(null);
  };

  return (
    <AuthContext.Provider value={{
      user,
      session,
      profile,
      business,
      subscription,
      isSubscriptionActive,
      isEmailVerified,
      loading,
      signUp,
      signIn,
      signInWithGoogle,
      signOut,
      refreshProfile,
      refreshSubscription,
      sendVerificationEmail,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
